import {
  FINDING_STATUS_LABELS,
  type InspectionItem,
  type InspectionStatus,
  type Progress,
  groupInspection,
  isComplete,
  progressOf,
} from '@sivan/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { ResultSegment } from '../ui/ResultSegment';
import type { DraftFinding } from './handover-draft';
import { INSPECTION_SEGMENTS, chosen } from './results';

// The structured apartment inspection (H-Checklist): rooms → work categories → items, each marked
// תקין / לא תקין / לא רלוונטי (טרם נבדק until then). The same panel serves the initial and the final
// handover; each has its own items.

export function ProgressBar({
  progress,
  label,
}: {
  readonly progress: Progress;
  readonly label: string;
}) {
  const percent = progress.total === 0 ? 0 : Math.round((progress.checked / progress.total) * 100);
  return (
    <div
      className="progress"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={progress.total}
      aria-valuenow={progress.checked}
      aria-valuetext={`נבדקו ${String(progress.checked)} מתוך ${String(progress.total)}`}
    >
      <span className="progress__bar" style={{ width: `${String(percent)}%` }} />
    </div>
  );
}

const countText = (p: Progress) => `${String(p.checked)}/${String(p.total)}`;

export function InspectionPanel({
  items,
  results,
  findings,
  readOnly,
  notice,
  onMark,
  onAllOk,
  onAddFinding,
  onEditFinding,
  onCloseFinding,
  onReopenFinding,
  focusRoom = null,
}: {
  readonly items: readonly InspectionItem[];
  readonly results: Readonly<Record<string, InspectionStatus>>;
  readonly findings: readonly DraftFinding[];
  readonly readOnly: boolean;
  /** A message about the last action (for example, items left unmarked by "הכל תקין"). */
  readonly notice: string | null;
  readonly onMark: (item: InspectionItem, status: Exclude<InspectionStatus, 'unchecked'>) => void;
  readonly onAllOk: (items: readonly InspectionItem[], area: string, category: string) => void;
  readonly onAddFinding: (item: InspectionItem) => void;
  readonly onEditFinding: (key: string) => void;
  readonly onCloseFinding: (finding: DraftFinding) => void;
  readonly onReopenFinding: (finding: DraftFinding) => void;
  /** A room to open and focus (from the inspection summary); a new object each time. */
  readonly focusRoom?: { readonly areaId: string } | null;
}) {
  const current = items.map((i) => ({ ...i, status: results[i.id] ?? i.status }));
  const areas = groupInspection(current);
  const areaItems = (areaId: string) => current.filter((i) => i.areaId === areaId);
  const firstOpen =
    areas.find((a) => !isComplete(progressOf(areaItems(a.areaId))))?.areaId ?? areas[0]?.areaId;
  const [roomId, setRoomId] = useState<string | undefined>(firstOpen);
  const room = areas.find((a) => a.areaId === roomId) ?? areas[0];
  const roomIndex = room === undefined ? -1 : areas.indexOf(room);
  const titleId = useId();
  const roomTitle = useRef<HTMLHeadingElement>(null);
  const moved = useRef(false);
  useEffect(() => {
    // After moving to another room, its title takes focus (and scrolls into view on a phone).
    if (!moved.current) return;
    roomTitle.current?.focus();
    roomTitle.current?.scrollIntoView({ block: 'start' });
  }, [roomId]);
  const go = (areaId: string | undefined) => {
    moved.current = true;
    setRoomId(areaId);
  };
  // A room requested from the summary opens at once, and its title takes focus.
  const [requested, setRequested] = useState(focusRoom);
  if (focusRoom !== requested) {
    setRequested(focusRoom);
    if (focusRoom !== null) setRoomId(focusRoom.areaId);
  }
  useEffect(() => {
    if (focusRoom === null) return;
    roomTitle.current?.focus();
    roomTitle.current?.scrollIntoView({ block: 'start' });
  }, [focusRoom]);

  const overall = progressOf(current);
  const openFindings = findings.filter((f) => f.inspectionItemId !== null && f.status === 'open');
  const incomplete = areas.filter((a) => !isComplete(progressOf(areaItems(a.areaId))));
  const findingsOf = (itemId: string) => findings.filter((f) => f.inspectionItemId === itemId);

  return (
    <section className="inspection editor-section" aria-labelledby={titleId}>
      <div className="inspection__head">
        <h2 id={titleId} className="section-title">
          בדיקת הדירה
        </h2>
        <ProgressBar progress={overall} label="התקדמות הבדיקה בדירה" />
        <p className="inspection__summary">
          נבדקו {overall.checked} מתוך {overall.total} פריטים
          {' · '}
          {openFindings.length === 1
            ? 'ליקוי פתוח אחד'
            : `${String(openFindings.length)} ליקויים פתוחים`}
        </p>
        {incomplete.length > 0 && (
          <p className="notice notice--warning inspection__incomplete">
            {incomplete.length === 1
              ? 'חדר אחד טרם הושלם'
              : `${String(incomplete.length)} חדרים טרם הושלמו`}
            : {incomplete.map((a) => a.area).join(', ')}. חדר מושלם רק כשלכל פריט יש תוצאה.
          </p>
        )}
        {notice !== null && (
          <p className="notice inspection__notice" role="status">
            {notice}
          </p>
        )}
      </div>

      <nav className="room-nav" aria-label="חדרים בדירה">
        <ul>
          {areas.map((area) => {
            const p = progressOf(areaItems(area.areaId));
            const state = p.defects > 0 ? 'defect' : isComplete(p) ? 'done' : 'open';
            const stateText =
              state === 'defect'
                ? `${String(p.defects)} לא תקין`
                : state === 'done'
                  ? 'הושלם'
                  : p.checked === 0
                    ? 'ממתין לבדיקה'
                    : 'בבדיקה';
            return (
              <li key={area.areaId}>
                <button
                  type="button"
                  className={`room-nav__room room-nav__room--${state}`}
                  aria-current={area.areaId === room?.areaId ? 'step' : undefined}
                  onClick={() => {
                    go(area.areaId);
                  }}
                >
                  <span className="room-nav__name">{area.area}</span>
                  <span className="room-nav__count">{countText(p)}</span>
                  <span className="room-nav__state">{stateText}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {room !== undefined && (
        <section className="room" aria-labelledby={`${titleId}-room`}>
          <h3 id={`${titleId}-room`} className="room__title" ref={roomTitle} tabIndex={-1}>
            {room.area}{' '}
            <span className="count">({countText(progressOf(areaItems(room.areaId)))})</span>
          </h3>
          {room.categories.map((category) => {
            const p = progressOf(category.items);
            return (
              <section
                key={category.workCategoryId}
                className="inspection-category"
                aria-label={category.workCategory}
              >
                <header className="inspection-category__head">
                  <h4 className="inspection-category__title">{category.workCategory}</h4>
                  <span className="inspection-category__count">{countText(p)}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      className={`button all-ok${category.items.every((i) => i.status === 'ok') ? ' all-ok--on' : ''}`}
                      aria-label={`הכל תקין: ${category.workCategory} ב${room.area}`}
                      onClick={() => {
                        onAllOk(category.items, room.area, category.workCategory);
                      }}
                    >
                      ✓ הכל תקין
                    </button>
                  )}
                </header>
                <ul className="check-list">
                  {category.items.map((item) => {
                    const linked = findingsOf(item.id);
                    return (
                      <li
                        key={item.id}
                        className={`check check--${item.status}`}
                        aria-label={item.name}
                      >
                        <div className="check__row">
                          <span className="check__name">
                            {item.name}
                            {item.status === 'unchecked' && (
                              <span className="chip chip--pending">טרם נבדק</span>
                            )}
                          </span>
                          <ResultSegment
                            label={`תוצאת בדיקה: ${item.name}`}
                            segments={INSPECTION_SEGMENTS}
                            value={chosen(item.status)}
                            disabled={readOnly}
                            onChoose={(status) => {
                              onMark(item, status);
                            }}
                          />
                        </div>
                        {linked.length > 0 && (
                          <ul className="check__findings" aria-label={`ליקויים: ${item.name}`}>
                            {linked.map((f) => (
                              <li
                                key={f.key}
                                className={`check-finding check-finding--${f.status}`}
                              >
                                <span className={`chip chip--finding-${f.status}`}>
                                  {FINDING_STATUS_LABELS[f.status]}
                                </span>
                                <span className="check-finding__text">
                                  {f.description}
                                  {f.contractor.trim() !== '' && (
                                    <span className="hint"> · {f.contractor}</span>
                                  )}
                                  {f.photos.length > 0 && (
                                    <span className="hint"> · {f.photos.length} תמונות</span>
                                  )}
                                  {f.status !== 'open' && f.resolutionNote.trim() !== '' && (
                                    <span className="hint"> · {f.resolutionNote}</span>
                                  )}
                                </span>
                                {!readOnly && (
                                  <span className="check-finding__actions">
                                    <button
                                      type="button"
                                      className="button button--small"
                                      aria-label={`עריכת ליקוי: ${f.description}`}
                                      onClick={() => {
                                        onEditFinding(f.key);
                                      }}
                                    >
                                      עריכה
                                    </button>
                                    {f.status === 'open' ? (
                                      <button
                                        type="button"
                                        className="button button--small"
                                        aria-label={`סגירת ליקוי: ${f.description}`}
                                        onClick={() => {
                                          onCloseFinding(f);
                                        }}
                                      >
                                        סגירה
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="button button--small"
                                        aria-label={`פתיחה מחדש: ${f.description}`}
                                        onClick={() => {
                                          onReopenFinding(f);
                                        }}
                                      >
                                        פתיחה מחדש
                                      </button>
                                    )}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        {!readOnly && item.status === 'defect' && (
                          <button
                            type="button"
                            className="button button--small check__more"
                            aria-label={`ליקוי נוסף: ${item.name}`}
                            onClick={() => {
                              onAddFinding(item);
                            }}
                          >
                            + ליקוי נוסף
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
          <div className="room__steps">
            <button
              type="button"
              className="button"
              disabled={roomIndex <= 0}
              onClick={() => {
                go(areas[roomIndex - 1]?.areaId);
              }}
            >
              → החדר הקודם
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={roomIndex >= areas.length - 1}
              onClick={() => {
                go(areas[roomIndex + 1]?.areaId);
              }}
            >
              החדר הבא ←
            </button>
          </div>
        </section>
      )}
    </section>
  );
}

export interface Closing {
  readonly key: string;
  readonly status: 'resolved' | 'withdrawn';
  readonly note: string;
}

/**
 * Closing findings explicitly, never deleting them: each one is resolved (fixed) or withdrawn
 * (recorded in error, with a reason). Used to mark an item OK or not relevant while it has open
 * findings, and to close one finding.
 */
export function ResolveForm({
  findings,
  submitLabel,
  intro,
  onConfirm,
  onCancel,
}: {
  readonly findings: readonly DraftFinding[];
  readonly submitLabel: string;
  readonly intro: string;
  readonly onConfirm: (closing: Closing[]) => void;
  readonly onCancel: () => void;
}) {
  const [choices, setChoices] = useState<Record<string, Closing>>(() =>
    Object.fromEntries(findings.map((f) => [f.key, { key: f.key, status: 'resolved', note: '' }])),
  );
  const [showErrors, setShowErrors] = useState(false);
  const id = useId();
  const missing = (c: Closing | undefined) => c?.status === 'withdrawn' && c.note.trim() === '';
  return (
    <form
      className="resolve-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const list = findings.map((f) => choices[f.key]).filter((c) => c !== undefined);
        if (list.some(missing)) {
          setShowErrors(true);
          return;
        }
        onConfirm(list);
      }}
    >
      <p>{intro}</p>
      {findings.map((f, index) => {
        const choice = choices[f.key];
        const set = (change: Partial<Closing>) => {
          setChoices((all) => {
            const before = all[f.key];
            return before === undefined ? all : { ...all, [f.key]: { ...before, ...change } };
          });
        };
        const noteError = showErrors && missing(choice);
        return (
          <fieldset key={f.key} className="resolve-form__finding">
            <legend>{f.description}</legend>
            <div className="resolve-form__choices">
              {(['resolved', 'withdrawn'] as const).map((status) => (
                <label key={status} className="choice">
                  <input
                    type="radio"
                    name={`${id}-${String(index)}`}
                    checked={choice?.status === status}
                    onChange={() => {
                      set({ status });
                    }}
                  />
                  {status === 'resolved' ? 'תוקן' : 'נרשם בטעות'}
                </label>
              ))}
            </div>
            <label className="field">
              <span>{choice?.status === 'withdrawn' ? 'מדוע נרשם בטעות' : 'הערה (רשות)'}</span>
              <textarea
                rows={2}
                value={choice?.note ?? ''}
                aria-invalid={noteError}
                aria-describedby={noteError ? `${id}-${String(index)}-error` : undefined}
                onChange={(e) => {
                  set({ note: e.target.value });
                }}
              />
              {noteError && (
                <p className="field__error" id={`${id}-${String(index)}-error`}>
                  יש לציין מדוע הליקוי נרשם בטעות
                </p>
              )}
            </label>
          </fieldset>
        );
      })}
      <p className="hint">הליקויים נשמרים בפרוטוקול ובהיסטוריה; הם אינם נמחקים.</p>
      <div className="form-actions">
        <button type="submit" className="button button--primary">
          {submitLabel}
        </button>
        <button type="button" className="button" onClick={onCancel}>
          ביטול
        </button>
      </div>
    </form>
  );
}
